import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/ui/native-select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errorMessage";
import { gerarRomaneioPDF } from "@/lib/romaneioPDF";
import { STATUS_ROMANEIO, fmtDataBR, fmtHora, linkConferencia } from "./constants";
import { Printer, Send, XCircle, Lock, Link2, ArrowLeft, Pencil } from "lucide-react";

const fmtQ = (q) => (q === null || q === undefined ? "—" : Number(q).toLocaleString("pt-BR", { maximumFractionDigits: 2 }));

export default function RomaneioDetalhePage() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [dlgCancelar, setDlgCancelar] = useState(false);
  const [justificativa, setJustificativa] = useState("");
  const [dlgFechar, setDlgFechar] = useState(false);
  const [retorno, setRetorno] = useState([]);
  const [agindo, setAgindo] = useState(false);
  const [dlgEntrega, setDlgEntrega] = useState(false);
  const [entrega, setEntrega] = useState({ endereco_entrega: "", contato_obra: "", telefone_obra: "" });

  const abrirEntrega = () => {
    setEntrega({ endereco_entrega: r.endereco_entrega || "", contato_obra: r.contato_obra || "", telefone_obra: r.telefone_obra || "" });
    setDlgEntrega(true);
  };

  const salvarEntrega = async () => {
    setAgindo(true);
    try {
      const res = await axios.patch(`${API}/logistica/romaneios/${id}/entrega`, entrega);
      toast.success(res.data.message);
      setDlgEntrega(false);
      carregar();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao salvar dados de entrega")); }
    finally { setAgindo(false); }
  };

  const carregar = useCallback(() => {
    axios.get(`${API}/logistica/romaneios/${id}`).then(res => setR(res.data)).catch(() => toast.error("Romaneio não encontrado"));
  }, [id]);
  useEffect(() => { carregar(); }, [carregar]);

  if (!r) return <MainLayout><div className="py-16 text-center text-slate-400">Carregando…</div></MainLayout>;

  const st = STATUS_ROMANEIO[r.status] || {};
  const editavel = ["rascunho", "separado"].includes(r.status);

  const toggleSeparado = async (linha) => {
    try {
      const res = await axios.post(`${API}/logistica/romaneios/${id}/separar`, { linha_ids: [linha.linha_id], separado: !linha.separado });
      toast.success(res.data.message);
      carregar();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro na separação")); }
  };

  const separarTodas = async () => {
    try {
      const res = await axios.post(`${API}/logistica/romaneios/${id}/separar`, { linha_ids: r.linhas.map(l => l.linha_id), separado: true });
      toast.success(res.data.message);
      carregar();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro na separação")); }
  };

  const imprimir = async () => {
    try {
      await gerarRomaneioPDF(r, linkConferencia(r.conferencia_token || r.id));
      await axios.post(`${API}/logistica/romaneios/${id}/marcar-impresso`);
      carregar();
    } catch (err) { toast.error("Erro ao gerar PDF"); }
  };

  const despachar = async () => {
    setAgindo(true);
    try {
      const res = await axios.post(`${API}/logistica/romaneios/${id}/despachar`);
      toast.success(res.data.message);
      carregar();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao despachar")); }
    finally { setAgindo(false); }
  };

  const cancelar = async () => {
    setAgindo(true);
    try {
      const res = await axios.post(`${API}/logistica/romaneios/${id}/cancelar`, { justificativa });
      toast.success(res.data.message);
      setDlgCancelar(false);
      carregar();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao cancelar")); }
    finally { setAgindo(false); }
  };

  const abrirFechar = () => {
    if ((r.retorno_pendente || []).length) {
      setRetorno(r.retorno_pendente.map(x => ({ ...x, incluir: true })));
    } else {
      setRetorno(r.linhas.map(l => ({ incluir: false, descricao: l.nome, unidade: l.unidade, quantidade: l.tipo === "patrimonio" ? 1 : 0, estado: "B", tipo: l.tipo, ref_id: l.ref_id })));
    }
    setDlgFechar(true);
  };

  const fechar = async () => {
    setAgindo(true);
    try {
      const res = await axios.post(`${API}/logistica/romaneios/${id}/fechar`, {
        retorno: retorno.filter(x => x.incluir).map(({ incluir, ...x }) => ({ ...x, quantidade: Number(x.quantidade) || 0 })),
      });
      toast.success(res.data.message);
      setDlgFechar(false);
      carregar();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao fechar")); }
    finally { setAgindo(false); }
  };

  const copiarLink = () => {
    navigator.clipboard.writeText(linkConferencia(r.conferencia_token || r.id));
    toast.success("Link da conferência digital copiado (mesmo link do QR do PDF)");
  };

  return (
    <MainLayout>
      <div className="space-y-4" data-testid="romaneio-detalhe-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => (window.location.href = "/logistica/romaneios")} data-testid="btn-voltar"><ArrowLeft className="h-4 w-4" /></Button>
            <div>
              <h1 className="text-2xl font-bold text-luvcorp-navy">Romaneio {r.numero}</h1>
              <p className="text-sm text-slate-500">{r.obra_titulo} · entrega {fmtDataBR(r.data_entrega)}</p>
            </div>
            <Badge variant="outline" className={st.cls} data-testid="badge-status">{st.label}</Badge>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={copiarLink} data-testid="btn-link-conferencia"><Link2 className="h-4 w-4 mr-1" /> Conferência digital</Button>
            <Button variant="outline" size="sm" onClick={imprimir} data-testid="btn-imprimir-fl05"><Printer className="h-4 w-4 mr-1" /> Imprimir FL-05 (2 vias)</Button>
            {r.status === "separado" && (
              <Button size="sm" onClick={despachar} disabled={agindo} className="bg-luvcorp-navy text-white" data-testid="btn-despachar">
                <Send className="h-4 w-4 mr-1" /> Despachar
              </Button>)}
            {editavel && (
              <Button variant="outline" size="sm" onClick={abrirEntrega} data-testid="btn-editar-entrega">
                <Pencil className="h-4 w-4 mr-1" /> Editar entrega
              </Button>)}
            {editavel && (
              <Button variant="outline" size="sm" onClick={() => setDlgCancelar(true)} className="text-red-600 border-red-300" data-testid="btn-cancelar-romaneio">
                <XCircle className="h-4 w-4 mr-1" /> Cancelar
              </Button>)}
            {["entregue", "entregue_divergencia", "nao_confirmado"].includes(r.status) && (
              <Button size="sm" onClick={abrirFechar} className="bg-luvcorp-navy text-white" data-testid="btn-fechar-romaneio">
                <Lock className="h-4 w-4 mr-1" /> Conferir retorno e fechar
              </Button>)}
          </div>
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            {[["Obra / Condomínio", r.obra_titulo], ["Cliente", r.cliente_nome], ["Endereço de entrega", r.endereco_entrega],
              ["Contato / tel.", `${r.contato_obra || ""} ${r.telefone_obra ? "· " + r.telefone_obra : ""}`],
              ["Veículo / placa", r.veiculo_placa], ["Condutor", r.condutor_nome],
              ["Ordem na rota", r.ordem_rota_pos ? `${r.ordem_rota_pos} de ${r.ordem_rota_total || "—"}` : "—"],
              ["Origem", r.origem_descricao], ["Nº requisição", r.numero_requisicao || "—"],
              ["Saída base", r.hora_saida_base ? fmtHora(r.hora_saida_base) : "—"],
              ["Chegada obra", r.hora_chegada_obra ? fmtHora(r.hora_chegada_obra) : "—"],
              ["Recebido por", r.recebedor_nome ? `${r.recebedor_nome} · ${fmtHora(r.conferido_em)}` : "—"],
            ].map(([k, v]) => (
              <div key={k}><p className="text-xs text-slate-400 uppercase">{k}</p><p className="font-medium text-luvcorp-navy">{v || "—"}</p></div>
            ))}
            {r.cancelado_motivo && <div className="col-span-2 md:col-span-4 text-red-600 text-sm">Cancelado: {r.cancelado_motivo}</div>}
          </CardContent>
        </Card>

        <Dialog open={dlgEntrega} onOpenChange={setDlgEntrega}>
          <DialogContent data-testid="dlg-editar-entrega">
            <DialogHeader><DialogTitle>Editar dados de entrega</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Endereço de entrega (rua, número, bairro) *</Label>
                <Input value={entrega.endereco_entrega} onChange={(e) => setEntrega(x => ({ ...x, endereco_entrega: e.target.value }))}
                  placeholder="Ex.: Rua das Obras, 120, Botafogo" data-testid="input-editar-endereco" />
              </div>
              <div>
                <Label className="text-xs">Contato na obra (nome) *</Label>
                <Input value={entrega.contato_obra} onChange={(e) => setEntrega(x => ({ ...x, contato_obra: e.target.value }))} data-testid="input-editar-contato" />
              </div>
              <div>
                <Label className="text-xs">Telefone *</Label>
                <Input value={entrega.telefone_obra} onChange={(e) => setEntrega(x => ({ ...x, telefone_obra: e.target.value }))}
                  placeholder="(21) 99999-9999" data-testid="input-editar-telefone" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDlgEntrega(false)}>Cancelar</Button>
              <Button onClick={salvarEntrega} disabled={agindo} className="bg-luvcorp-navy text-white" data-testid="btn-salvar-entrega">Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-0 overflow-x-auto">
            <div className="flex items-center justify-between px-4 py-2">
              <p className="text-sm font-semibold text-luvcorp-navy">Itens ({r.linhas.length})</p>
              {editavel && r.linhas.some(l => !l.separado) && (
                <Button size="sm" variant="outline" onClick={separarTodas} data-testid="btn-separar-todas">Marcar todas como separadas</Button>)}
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs text-slate-500 border-y">
                  <th className="px-4 py-2">Tipo</th><th className="px-2 py-2">Item</th><th className="px-2 py-2">Unid.</th>
                  <th className="px-2 py-2">Qtd. enviada</th><th className="px-2 py-2 text-center">Separado ✓</th>
                  <th className="px-2 py-2">Qtd. recebida</th><th className="px-2 py-2">Conf.</th><th className="px-2 py-2">Obs.</th>
                </tr>
              </thead>
              <tbody>
                {r.linhas.map((l, i) => (
                  <tr key={l.linha_id} className="border-b border-slate-100" data-testid={`detalhe-linha-${i}`}>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className={l.tipo === "material" ? "bg-blue-50 text-blue-700 border-blue-300" : "bg-amber-50 text-amber-700 border-amber-300"}>
                        {l.tipo === "material" ? "MATERIAL" : "PATRIMÔNIO"}
                      </Badge>
                    </td>
                    <td className="px-2 py-2">{l.nome} <span className="text-xs text-slate-400">{l.codigo}</span></td>
                    <td className="px-2 py-2">{l.unidade}</td>
                    <td className="px-2 py-2">{fmtQ(l.quantidade)}</td>
                    <td className="px-2 py-2 text-center">
                      <Checkbox checked={!!l.separado} disabled={!editavel} onCheckedChange={() => toggleSeparado(l)} data-testid={`check-separado-${i}`} />
                    </td>
                    <td className="px-2 py-2">{l.qtd_recebida == null ? "—" : fmtQ(l.qtd_recebida)}</td>
                    <td className="px-2 py-2">{l.conferencia ? (
                      <Badge variant="outline" className={l.conferencia === "OK" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-red-50 text-red-700 border-red-300"}>{l.codigo_div ? `DIV · ${l.codigo_div}` : l.conferencia}</Badge>) : "—"}</td>
                    <td className="px-2 py-2 text-xs text-slate-500">{l.descricao_div || l.observacao || "—"}{l.fl01_folha ? <span className="block text-[10px] text-slate-400">FL-01 f.{l.fl01_folha} l.{l.fl01_linha}</span> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {(r.retorno || []).length > 0 && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm font-semibold text-luvcorp-navy mb-2">Retorno à base (sobras, devoluções e vazios)</p>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-slate-500 border-b"><th className="py-1">Item</th><th>Unid.</th><th>Qtd.</th><th>Estado</th></tr></thead>
                <tbody>{r.retorno.map((x, i) => (
                  <tr key={i} className="border-b border-slate-100"><td className="py-1.5">{x.descricao}</td><td>{x.unidade}</td><td>{x.quantidade}</td><td>{x.estado}</td></tr>))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-luvcorp-navy mb-2">Histórico</p>
            <div className="space-y-1">
              {(r.historico || []).map((h, i) => (
                <p key={i} className="text-xs text-slate-500">{fmtHora(h.em)} — <b>{h.acao}</b> {h.detalhe ? `· ${h.detalhe}` : ""} ({h.por})</p>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={dlgCancelar} onOpenChange={setDlgCancelar}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cancelar romaneio {r.numero}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Permitido só antes de "Em trânsito". A reserva é desfeita; nada é baixado.</p>
          <Textarea value={justificativa} onChange={(e) => setJustificativa(e.target.value)} placeholder="Justificativa (obrigatória)" data-testid="input-justificativa" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgCancelar(false)}>Voltar</Button>
            <Button onClick={cancelar} disabled={agindo || justificativa.trim().length < 5} className="bg-red-600 text-white" data-testid="btn-confirmar-cancelamento">Confirmar cancelamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlgFechar} onOpenChange={setDlgFechar}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Retorno à base — Romaneio {r.numero}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Marque o que voltou (sobras, devoluções e vazios), com estado B/R/D. Material em estado B reentra no estoque; patrimônio volta a Disponível no Depósito Central. Depois o documento fica travado.</p>
          <div className="max-h-72 overflow-auto space-y-2">
            {retorno.map((x, i) => (
              <div key={i} className="flex items-center gap-2 text-sm border-b border-slate-100 pb-1.5">
                <Checkbox checked={x.incluir} onCheckedChange={(v) => setRetorno(rs => rs.map((y, j) => j === i ? { ...y, incluir: !!v } : y))} data-testid={`check-retorno-${i}`} />
                <span className="flex-1">{x.descricao}</span>
                {x.tipo === "material" ? (
                  <Input type="number" min="0" step="any" value={x.quantidade} className="w-20 h-8"
                    onChange={(e) => setRetorno(rs => rs.map((y, j) => j === i ? { ...y, quantidade: e.target.value } : y))} />
                ) : <span className="w-20 text-center">1</span>}
                <NativeSelect value={x.estado} className="w-20" data-testid={`select-estado-retorno-${i}`}
                  onValueChange={(v) => setRetorno(rs => rs.map((y, j) => j === i ? { ...y, estado: v } : y))}
                  options={[{ value: "B", label: "B" }, { value: "R", label: "R" }, { value: "D", label: "D" }]} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgFechar(false)}>Voltar</Button>
            <Button onClick={fechar} disabled={agindo} className="bg-luvcorp-navy text-white" data-testid="btn-confirmar-fechamento"><Lock className="h-4 w-4 mr-1" /> Fechar romaneio</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
